import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IReleaseBodyMarkdownProps {
  body: string;
}

/**
 * GitHub-flavoured release body rendered as actual markdown rather than
 * preformatted text. Pinned styling so the inline render plays nicely with
 * the dense MOPS table layout — headings step down, lists keep their bullet,
 * external links open in a new tab and adopt the brand accent.
 */
const ReleaseBodyMarkdown = ({ body }: IReleaseBodyMarkdownProps) => (
  // `remark-gfm` here turns `- [ ]` lines into real `<input type="checkbox">`
  // elements (with the `task-list-item` class on the `<li>`). Styling notes:
  //   - `[&_li.task-list-item]:list-none` drops the bullet only on task rows
  //     so a regular `- ` bullet keeps its disc.
  //   - `accent-[#f79009]` tints the checkbox with the same warning amber
  //     used for `<code>` chips and the Unpublished-draft header.
  //   - `cursor-not-allowed` because the checkboxes are disabled — they're a
  //     visual aid for the operator running the steps elsewhere, not an
  //     interactive UI.
  // `hover:[&_a]` is wrong — that applies the hover color to every link
  // whenever the *parent div* is hovered. Per-link hover uses the
  // arbitrary-variant form `[&_a:hover]` so only the link the cursor is
  // actually over recolours.
  <div className="text-fg-primary [&_a]:text-fg-brand-primary [&_a:hover]:text-fg-brand-primary_alt [&_code]:text-fg-warning-primary text-sm leading-relaxed [&_a]:underline [&_a]:underline-offset-2 [&_code]:rounded-sm [&_code]:border [&_code]:border-[#61646c] [&_code]:bg-[#22262f] [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em] [&_code]:font-medium [&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-wide [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_input[type='checkbox']]:mr-2 [&_input[type='checkbox']]:h-3.5 [&_input[type='checkbox']]:w-3.5 [&_input[type='checkbox']]:cursor-not-allowed [&_input[type='checkbox']]:align-middle [&_input[type='checkbox']]:accent-[#f79009] [&_li.task-list-item]:list-none [&_li.task-list-item]:pl-0 [&_li]:my-0.5 [&_p]:my-1 [&_ul.contains-task-list]:pl-1 [&_ul]:list-disc [&_ul]:pl-5">
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, ...props }) => (
          <a href={href} target="_blank" rel="noreferrer noopener" {...props}>
            {children}
          </a>
        ),
      }}
    >
      {body}
    </Markdown>
  </div>
);

export default ReleaseBodyMarkdown;
